import { useRef, useState, useEffect } from "react";
import { LIMIT_TEXT_REPORT } from "../../../utils/vars";
import Spinner from "../../../global-components/src/components/spinner/spiner";
import axios from "axios";
import { useMessage } from "../../../global-components/src";
import { useAuth } from "../../../global-components/src";
import { PACOCA_API_URL } from "../../../global-components/src/utils/vars";
import { User } from "../../../global-components/src/types";
import { useTranslation } from "react-i18next";
import { usePasswordCache } from "../../../hooks/usePasswordCache";

interface UnbanUserProps {
    user: User | null;
    reasonRequired: boolean;
    onBanned: () => void
}

const UnbanUser: React.FC<UnbanUserProps> = ({ user, reasonRequired, onBanned }) => {
    const [text, setText] = useState("");
    const [isLimitExceeded, setIsLimitExceeded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [password, setPassword] = useState("");
    const { savePassword, getPassword, clearPassword } = usePasswordCache();
    const { setMessage } = useMessage();
    const { token } = useAuth();
    const { t } = useTranslation();
    const [errorsInput, setErrorsInput] = useState<any>({});
    const closeRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        const cached = getPassword();
        if (cached) setPassword(cached);
    }, []);

    const limitePost = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newText = event.target.value;
        setText(newText);
        setIsLimitExceeded(newText.length > LIMIT_TEXT_REPORT);
    }
 

    const handleUnbanUser = async () =>{
        setError("")
        setErrorsInput("");
        setIsLoading(true)
        const url = PACOCA_API_URL + `/users/unban/${user?.id}`;
        
        try {
            let errors: Record<string, string[]> = {};
            if (!text) {
                errors.reason = [t("errors.input.required", { field: t("generics.message") })];
            }
            if(text.length <=2 || text.toLowerCase() == "regras" || text.toLowerCase() == "regra"){
                errors.reason = [t("errors.input.minLength", { field: t("generics.message"), min: 3 })]
            }
            if(!password){
                errors.password = [t("errors.input.required", { field: t("inputs.password") })]
            }
        
            if (Object.keys(errors).length > 0) {
                throw {
                    response: {
                        data: {
                            errors
                        }
                    }
                };
            }

            const response = await axios.post(url, {password, id_user: user?.id, reason: text}, {
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            savePassword(password);

            if(closeRef.current)
                closeRef.current.click()

            setText("")
            setPassword("")
            setMessage(t("moderation.unban.success"), "success");
            onBanned()
        } catch (err: any) {
            if (err?.response?.data.errors) {
                setErrorsInput(err.response.data.errors); // Atualiza o estado com os erros
            } else if (err.response) {
                console.error("Erro no servidor:", err.response.status, err.response.data);
                if (err.response.status === 401) {
                    setError(t("generics.errors.sessionExpired"))
                }
                else if (err.response.status === 429 && err.response.data.message) {
                    setError(t("generics.errors.tooManyRequests", { minutes: err.response.data.remaining ?? 5 }))
                }
                else if (err.response.status === 429) {
                    setError(t("generics.errors.tooManyRequestsGeneric"));
                }
                else if (err.response) {
                    // O servidor respondeu com um código de erro
                    console.error("Erro no servidor:", err.response.status, err.response.data);
                    setError(err.response.data.message || "Erro ao fazer post!")
                }
            }else if (err) {
                // O servidor respondeu com um código de erro
                console.error("Erro no servidor:", err);
                setError(err.message || "Erro ao fazer post!")
            }
        } finally{
            setIsLoading(false)
        }
    }

    const handlePassword = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newText = event.target.value;
        setPassword(newText); // Certifique-se de que está atualizando a variável correta
    };

    
    return (
        <div className="modal fade" id="modal-desbanir" tabIndex={-1} aria-labelledby="modal-desbanir" aria-hidden="true">
            <div className="modal-dialog cover-mobile">
                <div className="modal-content">
                <div className="modal-header">
                    <h5 className="modal-title" id="modal-desbanir">Desbanir {user?.name.split(" ")[0]}</h5>
                    <button ref={closeRef} type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                    <div className="modal-body">
                        <p>Tem certeza que deseja desbanir {user?.name.split(" ")[0]}?</p>

                        <label htmlFor="time" className="mt-3 label-required">Sua senha</label>
                        <input name="username" autoComplete="username" className="d-none"/> {/* Campo oculto para evitar remover warning */}
                        <input value={password ?? ""} onChange={handlePassword} required className={`form-control ${errorsInput.password ? 'is-invalid' : ''}`} max="10" type="password"/>
                        {errorsInput.password && (
                            <div className="invalid-feedback" style={{display: "block"}}>{errorsInput.password[0]}</div> // Exibe a mensagem de erro
                        )}

                        <p className="mt-3">Motivo</p>
                        <textarea style={{ color: isLimitExceeded ? "red" : "inherit" }}  onChange={(e) => limitePost(e)}  value={text} className={`${errorsInput.reason ? 'is-invalid' : ''} form-control`}  name="motivo" id="motivo" cols={30} rows={10}></textarea>
                        {errorsInput.reason && (
                            <div className="invalid-feedback" style={{display: "block"}}>{errorsInput.reason[0]}</div> // Exibe a mensagem de erro
                        )}

                        <div className="user-select-none limite-denuncia">
                            {text.length}/{LIMIT_TEXT_REPORT}
                        </div>

                        <div className="invalid-feedback invalid-feedback-comment-text" style={{display: "block"}}>
                            {error}
                        </div>

                        <div className="modal-footer modal-footer-modal">
                            <button type="button" className="btn btn-secondary btn-modal" data-bs-dismiss="modal">Cancelar</button>
                            <button disabled={isLimitExceeded || isLoading} onClick={handleUnbanUser} type="submit" className="btn btn-blue btn-modal">
                                {!isLoading ? (
                                    <>Desbanir</>
                                ) : (
                                    <Spinner/>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UnbanUser;